import { createElement, lazy, useState, type ComponentType } from 'react'

/**
 * A page loaded on first use instead of with the entry chunk.
 *
 * Pages export named components, so this picks one by name. Render the result
 * inside a `<Suspense>`; `AppLayout` and the standalone routes in `App.tsx`
 * provide one.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- a page's props are its own; the registry types them as `any`.
export const lazyPage = <M extends Record<string, any>, K extends keyof M & string>(
  load: () => Promise<M>,
  name: K,
): ComponentType<React.ComponentProps<M[K]>> =>
  lazy(() => load().then((module) => ({ default: module[name] }))) as unknown as ComponentType<React.ComponentProps<M[K]>>

/**
 * A lazy page that can be fetched ahead (on hover, before a navigation).
 * Once its module is in, a new mount renders it directly instead of through
 * `lazy`, which would still suspend for a tick and flash the `<Suspense>`
 * fallback. Each mount keeps the path it started with, so a page that
 * mounted through `lazy` never remounts.
 */
export type PreloadablePage<P> = ComponentType<P> & { preload: () => Promise<void> }

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same as lazyPage.
export const preloadablePage = <M extends Record<string, any>, K extends keyof M & string>(
  load: () => Promise<M>,
  name: K,
): PreloadablePage<React.ComponentProps<M[K]>> => {
  type Props = React.ComponentProps<M[K]>
  let loaded: ComponentType<Props> | null = null
  let pending: Promise<void> | null = null
  const preload = () => {
    pending ??= load()
      .then((module) => {
        loaded = module[name] as ComponentType<Props>
      })
      .catch((error) => {
        pending = null
        throw error
      })
    return pending
  }
  const Lazy = lazy(() => preload().then(() => ({ default: loaded as ComponentType<Props> })))
  const Page = (props: Props) => {
    const [Direct] = useState(() => loaded)
    return Direct ? createElement(Direct, props) : createElement(Lazy as ComponentType<Props>, props)
  }
  return Object.assign(Page, { preload })
}
